import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { BreadcrumbItem } from '@/types/ui.types'
import { ChevronRight } from 'lucide-react'

export interface PageHeaderProps {
  title: string
  description?: string
  breadcrumb?: BreadcrumbItem[]
  actions?: ReactNode
  className?: string
  badge?: string
  icon?: ReactNode
}

export function PageHeader({
  title,
  description,
  breadcrumb,
  actions,
  className,
  badge,
  icon,
}: PageHeaderProps) {
  return (
    <div className={cn('flex flex-col mb-6', className)}>
      {/* Breadcrumb */}
      {breadcrumb && breadcrumb.length > 0 && (
        <nav style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 10 }}>
          {breadcrumb.map((item, i) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {i > 0 && (
                <ChevronRight size={10} style={{ color: 'var(--tx4)', flexShrink: 0 }} />
              )}
              {item.href ? (
                <Link
                  to={item.href}
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--tx3)',
                    textDecoration: 'none',
                    transition: 'color .15s',
                    fontFamily: '"Raleway", sans-serif',
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = 'var(--blue)')
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLElement).style.color = 'var(--tx3)')
                  }
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--tx4)',
                    fontFamily: '"Raleway", sans-serif',
                  }}
                >
                  {item.label}
                </span>
              )}
            </span>
          ))}
        </nav>
      )}

      {/* Title row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
          {icon && (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                flexShrink: 0,
                background: 'var(--surface-2)',
                border: '0.5px solid var(--bd)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--blue)',
                marginTop: 1,
              }}
            >
              {icon}
            </div>
          )}

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              {/* h1 — 20px, weight 600 (Linear-style, pas 800) */}
              <h1
                style={{
                  fontFamily: '"Raleway", sans-serif',
                  fontWeight: 600,
                  fontSize: 20,
                  color: 'var(--tx)',
                  letterSpacing: '-0.02em',
                  margin: 0,
                  lineHeight: 1.25,
                }}
              >
                {title}
              </h1>
              {badge && (
                <span
                  style={{
                    fontFamily: '"Raleway", sans-serif',
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    padding: '2px 8px',
                    borderRadius: 99,
                    background: 'var(--blue-bg)',
                    color: 'var(--blue-d)',
                    border: '0.5px solid rgba(245,124,0,0.22)',
                  }}
                >
                  {badge}
                </span>
              )}
            </div>
            {description && (
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: 13,
                  color: 'var(--tx3)',
                  lineHeight: 1.55,
                  fontFamily: '"Raleway", sans-serif',
                  fontWeight: 400,
                }}
              >
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        {actions && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  )
}

export interface PageContainerProps {
  children: ReactNode
  className?: string
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
}

export function PageContainer({ children, className, maxWidth = 'full' }: PageContainerProps) {
  const mw = {
    sm: 'max-w-2xl',
    md: 'max-w-4xl',
    lg: 'max-w-5xl',
    xl: 'max-w-6xl',
    '2xl': 'max-w-7xl',
    full: 'max-w-full',
  }
  return (
    <div className={cn('mx-auto w-full', mw[maxWidth], className)}>{children}</div>
  )
}
